/**
 * AgreementsArea — "who signs what, in what order, and is it executed?"
 *
 * THE WITNESS AGREEMENT IS DRAWN AS THE HIRE PURCHASE AGREEMENT'S DEPENDENT, NOT AS ITS PEER, and
 * that is the single biggest confusion this page inherits. The board this replaces showed the two
 * side by side as equals, and admins spent days trying to send the second one.
 *
 * WHAT IT DEPENDS ON IS THE DRIVER'S SIGNATURE [D-B18], not the hire purchase agreement's
 * execution. The witness signs the WITNESS agreement and nothing else; that instrument prints the
 * date the driver signed theirs, so `agreement_issue_token` withholds the witness's link until a
 * driver signature exists — on the live document's DRIVER seat, or on the legacy row a paper
 * original was typed onto. Waiting for the hire purchase agreement to be EXECUTED would be
 * circular: Primelane cannot countersign it until the witness agreement has been signed.
 *
 * THE LOCK IS THE SLOT'S OWN EVIDENCE, so this block, the manifest's witness row and the board's
 * status line are three readings of one derivation (`buildBoardSlots`) rather than three
 * derivations of one rule.
 *
 * THE PARTIES BLOCK IS THE OTHER DEFECT CLOSED HERE. The witness agreement prints the witness's
 * name, phone AND address — and the create form accepted a name alone, so a witness could satisfy
 * the picker and then block a send for a reason no surface named. The parties now stand in signing
 * order with what each one prints beside them, and the two fields that block the send ARE EDITED
 * HERE, in place, on the same quiet-field/commit-bar rules the money clauses use. The manifest's
 * Parties row names `f-witnessAddress` as its focus target, and until this landed that id belonged
 * to an empty `<span>`: the jump scrolled the block into view and put the caret nowhere, which made
 * "Fix" a link to a page that could not fix anything.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';

import { Button } from '@/components/ui/button';

import AgreementSlotList, {
    type ContractAgreementsModel,
} from '@/components/agreements/AgreementSlotList';
import { PARTNER_SCHEDULE_LOCK_REASON, type BoardSlot } from '@/components/agreements/boardSlots';
import { agreementBoardContractKey } from '@/components/agreements/useContractAgreements';

import { WITNESSES_KEY, createWitness, fetchWitnesses, saveWitnessContact } from '../data/witness';
import {
    WITNESS_CREATE_REQUIREMENT,
    availableWitnesses,
    validateWitnessDraft,
    witnessContactStatement,
    witnessLine,
    witnessMeta,
    witnessSearchText,
    type WitnessDraft,
    type WitnessOption,
} from '../data/witnessDraft';
import InlineFieldGroup from '../inline/InlineFieldGroup';
import InlinePickerPopover, {
    PickerPanelActions,
    PickerPanelField,
    PickerPanelNote,
    type PickerRow,
} from '../inline/InlinePickerPopover';
import QuietField from '../inline/QuietField';
import type { FieldValues } from '../inline/inlineEditReducer';
import { useInlineGroup } from '../inline/useInlineGroup';
import type { DriverFacts, VehicleFacts, WitnessFacts } from '../setupState';
import type { SetupArea } from '../steps';
import { Block, Blocks, Hint, LinkLike, LockLine, Rec, Rows } from './blocks';

interface Props {
    contractId: number;
    /**
     * `partitionSlotsByStep(agreements.slots).agreements` — the hire purchase agreement, the
     * witness agreement and, on a partner's vehicle, the Vehicle Schedule: the instruments the
     * contract itself is the subject of.
     *
     * THE SCHEDULE ARRIVES THROUGH THE PARTITION'S DEFAULT AND NOT THROUGH A NEW BRANCH [D-B48].
     * `stepForDocType` answers `'agreements'` for every type it does not name, so a
     * `PARTNER_MANAGEMENT` slot lands here the moment `buildBoardSlots` mints one — which is the
     * behaviour that keeps the partition total over the doc-type enum.
     *
     * NO STANDING ADVANCES REACH IT [D-B17], AND THAT IS ABOUT THE SCOPE RATHER THAN THE TYPE.
     * `buildBoardSlots` mints a LOAN slot only for a caller that hands it `loans`, and the subjects
     * read issues no such query at `'setup'` — so this page's partition can never hold one, while
     * the `'all'` board on Contract Insights does. `stepForDocType('LOAN')` still answers
     * `agreements`, so the partition stays total over the type wherever it is applied.
     */
    slots: BoardSlot[];
    /** The DEPOSIT slots, mirrored one line each. */
    depositSlots: BoardSlot[];
    /** The INSURANCE_LOAN slots, mirrored one line each. */
    insuranceSlots: BoardSlot[];
    agreements: ContractAgreementsModel;
    driver: DriverFacts;
    witness: WitnessFacts | null;
    /**
     * The contract's car, read for ONE sentence: which of the two reasons the schedule block is
     * empty for [D-B48].
     *
     * `buildBoardSlots` mints no `PARTNER_MANAGEMENT` slot for the house AND none for a contract
     * that names no vehicle — `isThirdPartyVehicle` answers false for both — so the slot's absence
     * cannot tell the two apart, and a block that read only the absence told an admin who had not
     * reached the vehicle step that Primelane owns a car they have not chosen yet.
     */
    vehicle: VehicleFacts;
    onGoToArea: (area: SetupArea) => void;
}

/**
 * The witness create panel, seeded once from whatever failed to match.
 *
 * IT OWNS ITS OWN STATE rather than living in three `useState`s upstairs, because the picker's
 * render prop runs on every keystroke of the SEARCH box too — a value derived from `typed` inline
 * would keep reappearing after it was deleted. Mounting seeds the name; after that the fields are
 * the admin's.
 *
 * THE BUTTON IS DISABLED BY THE SAME RULE THE WRITE ENFORCES. `witnessDraftComplete` and
 * `createWitness` both call `validateWitnessDraft`, so a disabled control and a refused insert can
 * never say different things about what a witness is.
 */
function NewWitnessPanel({
    typed,
    pending,
    refusal,
    onCreate,
    onCancel,
}: {
    typed: string;
    pending: boolean;
    refusal: string | null;
    onCreate: (draft: WitnessDraft) => void;
    onCancel: () => void;
}) {
    const [name, setName] = useState(typed);
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [relationship, setRelationship] = useState('');
    const invalid = validateWitnessDraft({ name, phone, address });

    return (
        <>
            <PickerPanelField id="f-newWitnessName" label="Full name">
                <input
                    id="f-newWitnessName"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder="As it will print"
                />
            </PickerPanelField>
            <PickerPanelField id="f-newWitnessPhone" label="Phone">
                <input
                    id="f-newWitnessPhone"
                    inputMode="tel"
                    value={phone}
                    onChange={(event) => setPhone(event.target.value)}
                    placeholder="+233…"
                />
            </PickerPanelField>
            <PickerPanelField id="f-newWitnessAddress" label="Address">
                <input
                    id="f-newWitnessAddress"
                    value={address}
                    onChange={(event) => setAddress(event.target.value)}
                    placeholder="Where they live"
                />
            </PickerPanelField>
            {/* THE OPTIONAL ONE SAYS SO IN THE PLACEHOLDER, not in a second requirement line. The
                panel already carries one sentence about what a witness cannot be saved without,
                and a second one about what they can would make both of them read as rules. */}
            <PickerPanelField id="f-newWitnessRelationship" label="Relationship">
                <input
                    id="f-newWitnessRelationship"
                    value={relationship}
                    onChange={(event) => setRelationship(event.target.value)}
                    placeholder="Optional — how they know the hirer"
                />
            </PickerPanelField>
            {refusal && <PickerPanelNote bad>{refusal}</PickerPanelNote>}
            <PickerPanelActions>
                <Button variant="ghost" size="sm" onClick={onCancel}>
                    Back
                </Button>
                <Button
                    size="sm"
                    disabled={!!invalid || pending}
                    onClick={() => onCreate({ name, phone, address, relationship })}
                >
                    Create and choose
                </Button>
            </PickerPanelActions>
        </>
    );
}

/** How a mirrored instrument reads in one line: what state it is in, nothing more. */
function mirrorState(slot: BoardSlot): string {
    if (slot.legacySignedAt && slot.legacyUrl) {
        return `Executed ${format(new Date(slot.legacySignedAt), 'MMM d, yyyy')}`;
    }
    const document = slot.document;
    if (!document) return '';
    if (document.status === 'COMPLETED') {
        return document.finalized_at ? 'Executed · PDF filed' : 'Signed by everyone · sealing';
    }
    const signed = document.signatures.filter((signature) => signature.status === 'SIGNED').length;
    return `${signed} of ${document.signatures.length} signed`;
}

export default function AgreementsArea({
    contractId,
    slots,
    depositSlots,
    insuranceSlots,
    agreements,
    driver,
    witness,
    vehicle,
    onGoToArea,
}: Props) {
    const hpa = slots.find((slot) => slot.docType === 'HIRE_PURCHASE') ?? null;
    const witnessSlot = slots.find((slot) => slot.docType === 'WITNESS') ?? null;
    /**
     * The Vehicle Schedule's slot, and ITS ABSENCE IS THE ANSWER TO A QUESTION rather than a
     * missing read [D-B48].
     *
     * `buildBoardSlots` mints a `PARTNER_MANAGEMENT` slot only where the contract's vehicle belongs
     * to a third-party partner — the server refuses to raise the document on one of Primelane's own
     * cars (`AGREEMENT_PARTNER_IS_HOUSE`), so a slot on a house vehicle would be a row whose only
     * button is a toast. This block therefore reads ownership off the slot list exactly as the
     * witness block reads the driver's signature off `driverSigned`: one derivation, in
     * `buildBoardSlots`, from the rows the server itself reads. A second one here — fetching the
     * vehicle's partner and comparing it against the house id — is the twin that [D-B18] removed
     * from this file once already.
     */
    const partnerSlot = slots.find((slot) => slot.docType === 'PARTNER_MANAGEMENT') ?? null;

    /**
     * THE LOCK TWIN IS GONE [D-B18]. This block used to re-derive the dependency from the witness
     * document's own `missing[]` — a second fetch and a second rule — and the two could disagree the
     * moment either moved. `driverSigned` is derived once, in `buildBoardSlots`, from the same rows
     * the server reads, and hangs on every slot the contract has.
     */
    const driverSigned = hpa?.driverSigned ?? witnessSlot?.driverSigned ?? false;
    const witnessLocked = !driverSigned;

    const hpaSignatures = hpa?.document?.signatures ?? [];
    const hpaSigned = hpaSignatures.filter((signature) => signature.status === 'SIGNED').length;
    const hpaPaper = !hpa?.document && !!hpa?.legacySignedAt;

    const witnessNeeds = [
        !witness?.id || !witness.name ? 'a name' : null,
        witness?.id && !witness.phone ? 'a phone' : null,
        witness?.id && !witness.address ? 'an address' : null,
    ].filter(Boolean) as string[];

    // ── The witness: picked, created and corrected in one row ────────────────
    const queryClient = useQueryClient();
    const witnessId = agreements.witnessId;

    const { data: allWitnesses, isLoading: witnessesLoading } = useQuery({
        queryKey: WITNESSES_KEY,
        queryFn: fetchWitnesses,
    });

    const pickedWitness =
        (allWitnesses ?? []).find((option) => option.id === witnessId) ?? null;

    /**
     * ONE OF THE THREE COMES FROM THE PEOPLE LIST RATHER THAN FROM THE SNAPSHOT, and that is not an
     * inconsistency to tidy away. The snapshot's witness is the board's contract read, whose embed
     * NAMES its columns (`contract_witnesses(id, name, phone, address)`) — adding `relationship` to
     * it would fail that read with 42703 wherever the migration has not landed and take the whole
     * agreements board down with it. `fetchWitnesses` star-selects for exactly this reason, so the
     * new field arrives there or not at all, harmlessly.
     *
     * A LATE ARRIVAL IS SAFE ON SCREEN: rule 1 of the inline reducer has a clean group follow the
     * server, so the field fills in when the list lands; and a group an admin has already typed
     * into keeps every character. It is NOT safe on the write by itself — a group that went dirty
     * before the list landed still holds an empty nobody typed — which is what the `save` below
     * is about.
     */
    const contactServer = useMemo<FieldValues>(
        () => ({
            witnessPhone: witness?.phone ?? '',
            witnessAddress: witness?.address ?? '',
            witnessRelationship: pickedWitness?.relationship ?? '',
        }),
        [witness?.phone, witness?.address, pickedWitness?.relationship],
    );

    /**
     * RULE 5 IS WHY THIS CARRIES A SUBJECT. Rule 1 keeps a draft alive through a refetch of the
     * SAME witness — which is right. Carrying it across a change of WHICH witness the row is about
     * is not: the phone in the field belongs to the person who was just replaced, and one Save
     * would write it onto the new one. Naming the subject makes the reducer drop it.
     */
    const contact = useInlineGroup({
        server: contactServer,
        subject: witnessId === null ? null : `witness:${witnessId}`,
        /**
         * THE LIST-BACKED FIELD IS WRITTEN ONLY WHERE SOMEBODY TYPED IT. Its server value comes
         * from `pickedWitness`, so while the list is in flight it reads empty because nothing has
         * ARRIVED — not because nothing is recorded — and reducer rule 1 then keeps that stale
         * empty for as long as the group stays dirty, list or no list. Asking WHO PUT THE VALUE
         * THERE rather than WHEN THE LIST ANSWERED is what separates an admin clearing a field
         * from a draft that was seeded before the read landed; `witnessContactStatement` holds
         * that rule, and holds it where it is testable.
         */
        save: (draft, touched) =>
            saveWitnessContact({ witnessId, ...witnessContactStatement(draft, touched) }),
        onSaved: () => {
            // The witness row is embedded in the board's contract read, and that entry is what
            // this block, the manifest's Parties row and the merge pre-flight all draw from.
            queryClient.invalidateQueries({ queryKey: agreementBoardContractKey(contractId) });
            queryClient.invalidateQueries({ queryKey: WITNESSES_KEY });
        },
    });

    const [creating, setCreating] = useState<string | null>(null);

    /**
     * Create the witness, then put this contract on them — in that order, in one gesture.
     *
     * THE VALIDATION IS ON THE WRITE, NOT ON THIS PANEL. `createWitness` calls
     * `validateWitnessDraft` and throws its sentence, so the legacy preparation form and this panel
     * cannot disagree about what a witness is. The panel's own copy of the rule below is only what
     * keeps the button from being pressable — a courtesy, never the guarantee.
     */
    const create = useMutation({
        mutationFn: async (draft: WitnessDraft) => {
            const id = await createWitness(draft);
            await agreements.setWitness.mutateAsync(id);
            return id;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: WITNESSES_KEY });
            setCreating(null);
        },
        onError: (raised: Error) => setCreating(raised.message || 'The witness was refused.'),
    });

    const witnessRows: PickerRow<WitnessOption>[] = availableWitnesses(
        allWitnesses ?? [],
        witnessId,
    ).map((option) => ({
        id: String(option.id),
        search: witnessSearchText(option),
        item: option,
    }));

    /**
     * Whoever the row is holding, from whichever read has arrived: the picked option, else the
     * contract's own copy of the witness — which is what keeps the trigger and its tooltip saying
     * the same thing while the list of people is still loading.
     */
    const witnessHeld: Pick<WitnessOption, 'name' | 'phone' | 'address'> | null =
        pickedWitness ??
        (witness?.name
            ? { name: witness.name, phone: witness.phone, address: witness.address }
            : null);

    return (
        <Blocks>
            <Block id="b-parties" title="Parties">
                <p className="sd-lede sd-dim">
                    In signing order, across two instruments: the driver signs the hire purchase
                    agreement, the witness then signs the witness agreement, and Primelane
                    countersigns both. Neither will send until every party it prints can be printed.
                </p>

                {/* THE INSTRUMENT EACH PARTY SIGNS GOES IN THE SUB-LINE, NOT THE LABEL. The key
                    column is a fixed 180px, so "1 · Hirer, on the hire purchase agreement" is three
                    wrapped lines of numeral — and the numeral is the part doing the work. */}
                <Rows>
                    <Rec
                        label="1 · Hirer"
                        value={driver.fullName}
                        sub={
                            <>
                                {[driver.phone, driver.address].filter(Boolean).join(' · ') ||
                                    'phone and address missing'}
                                {' · signs the hire purchase agreement · '}
                                <LinkLike onClick={() => onGoToArea('driver')}>Edit in Driver</LinkLike>
                            </>
                        }
                        empty="No driver assigned"
                    />
                </Rows>

                <InlineFieldGroup group={contact}>
                    <Rows>
                        {/* A PICK IS AN ACT. The witness is one foreign key on the contract; the two
                            fields below are values the witness agreement PRINTS, so they go through
                            the bar. That split is the canvas's rule, not this block's exception. */}
                        <InlinePickerPopover<WitnessOption>
                            id="f-witness"
                            label="2 · Witness"
                            rows={witnessRows}
                            selectedId={witnessId === null ? null : String(witnessId)}
                            /* THE TRIGGER IS AN IDENTITY, ITS TOOLTIP IS THE WHOLE LINE. One
                               witness is picked, so the name alone says which — the phone and the
                               address are already editable in the two rows below it, and printing
                               them on the trigger as well would say the same thing three times. */
                            selectedLabel={witnessHeld?.name ?? 'Chosen'}
                            selectedTitle={witnessHeld ? witnessLine(witnessHeld) : undefined}
                            emptyLabel="No witness chosen"
                            searchPlaceholder="Search people, or type a new name"
                            noun="witness"
                            renderLabel={(option) => option.name}
                            renderMeta={witnessMeta}
                            rowTitle={witnessLine}
                            /*
                             * 560, MEASURED AT 13px/11.5px INTER, not guessed. A row spends 70px on
                             * chrome and up to 12px more on a scrollbar, so the pair has to fit in
                             * `width − 82`. The longest realistic pair is a full Ghanaian name at
                             * 225px — `Emmanuel Kwabena Asante-Mensah` — beside a real contact line
                             * at 206px: `0244 123 456 · 12 Osu Badu St, Accra`. That is 431px, and
                             * 500 leaves 418 — so the meta ellipsed the street AND the name lost a
                             * glyph with it, because flex shrinkage is proportional and takes a
                             * little from every cell that can give. 560 leaves 478 and both fields
                             * print whole. A longer address still ellipses, which is the intended
                             * order: `.sd-pop-m` gives way eight times faster than the name.
                             */
                            width={560}
                            onSelect={(row) => agreements.setWitness.mutate(Number(row.id))}
                            onClear={() => agreements.setWitness.mutate(null)}
                            clearLabel="No witness on this contract"
                            loading={witnessesLoading}
                            disabled={agreements.setWitness.isPending}
                            hint="signs the witness agreement · picked from your people, or created here"
                            create={{
                                noun: 'witness',
                                requirement: WITNESS_CREATE_REQUIREMENT,
                                render: ({ typed, close, cancel }) => (
                                    <NewWitnessPanel
                                        typed={typed}
                                        pending={create.isPending}
                                        refusal={creating}
                                        onCancel={cancel}
                                        onCreate={(draft) =>
                                            create.mutate(draft, { onSuccess: () => close() })
                                        }
                                    />
                                ),
                            }}
                        />

                        {/* THE PLACEHOLDER CARRIES THE CONSEQUENCE, not the `format` hint: `.sd-fmt`
                            is `opacity: 0` until the field has focus, and what an empty one prints
                            on is exactly what an admin needs to read WITHOUT touching it. That
                            sentence was the read-only row's `empty` text and it does not get
                            quieter for becoming an input. */}
                        <QuietField
                            {...contact.field('witnessPhone')}
                            id="f-witnessPhone"
                            label="Witness phone"
                            inputMode="tel"
                            placeholder={
                                witnessId
                                    ? 'Missing — the witness agreement prints it'
                                    : 'Choose a witness first'
                            }
                            format="prints on the witness agreement"
                            disabled={!witnessId}
                        />
                        <QuietField
                            {...contact.field('witnessAddress')}
                            id="f-witnessAddress"
                            label="Witness address"
                            placeholder={
                                witnessId
                                    ? 'Missing — the witness agreement prints it'
                                    : 'Choose a witness first'
                            }
                            format="prints on the witness agreement"
                            disabled={!witnessId}
                        />
                        {/* THE OPTIONAL ONE, AND THE PLACEHOLDER IS WHERE IT SAYS SO. The two
                            above are missing when they are empty — the send is refused over them —
                            and this one is simply blank. Same commit bar, same write, different
                            consequence, and the only thing that can carry that difference on a row
                            whose value IS its control is what an empty one prints on. */}
                        <QuietField
                            {...contact.field('witnessRelationship')}
                            id="f-witnessRelationship"
                            label="Relationship"
                            placeholder={
                                witnessId ? 'Optional — how they know the hirer' : 'Choose a witness first'
                            }
                            format="e.g. Brother, Landlord"
                            disabled={!witnessId}
                        />
                        {/* THE CARVE-OUT IS NAMED WHERE IT APPLIES [D-B45/D-B27]. `agreement_sign`
                            holds Primelane's countersignature on the two contract instruments —
                            and on an insurance premium ADVANCE with them — until the driver and
                            the witness have both signed, because an advance is a third bargain
                            with credit exposure. An Insurance Premium Payment Record creates none:
                            nothing is advanced, no interest arises, nothing is owed. So the server
                            skips the witness limb for it, `siblingSignatureOutstanding` skips it on
                            the board, and this sentence has to say so or an admin will wait for a
                            signature nothing is waiting on. */}
                        <Rec
                            label="3 · Owner"
                            value="Primelane Ghana Ltd"
                            sub={
                                insuranceSlots[0]?.instrument === 'PAYMENT_RECORD'
                                    ? 'countersigns both instruments only once the driver has signed the hire purchase agreement and the witness the witness agreement — the insurance premium payment record asks for the driver’s signature alone'
                                    : 'countersigns both instruments — and the premium advance with them — only once the driver has signed the hire purchase agreement and the witness the witness agreement'
                            }
                        />
                    </Rows>
                </InlineFieldGroup>

                <Hint className="mt-3" >
                    {witnessNeeds.length > 0
                        ? `This witness is still missing ${witnessNeeds.join(' and ')}. A witness saved on a name alone satisfies the picker and then blocks the send, which is the defect these two fields exist to close.`
                        : 'Both fields belong to the witness rather than to this contract, so an edit reaches every contract they stand on. Changing WHO the witness is, while the contract is a draft, voids every document that seats a witness signature and revokes their signing links — the driver’s hire purchase agreement seats none, so it survives the swap.'}
                </Hint>
            </Block>

            {/* THE SCHEDULE SITS ABOVE THE HIRE PURCHASE AGREEMENT BECAUSE IT IS SIGNED BEFORE IT
                [D-B48]. On a partner's vehicle the driver's own seat is refused
                (`AGREEMENT_PRIOR_SIGNATURE_REQUIRED`) until the partner has executed this, so a
                canvas that put the master instrument first would be walking an admin down the page
                in the opposite order to the signatures. */}
            <Block id="b-partneragr" title="Vehicle schedule" aside="partner instrument">
                {partnerSlot ? (
                    <>
                        <p className="sd-lede sd-dim">
                            This vehicle belongs to a partner, and they sign before the driver can.
                            The schedule states the Cash price, the management fee this contract
                            charges and what the partner should expect back — figures drawn from the
                            money clauses, so a correction there is a new schedule rather than an
                            edit to this one.
                        </p>
                        <AgreementSlotList
                            slots={[partnerSlot]}
                            agreements={agreements}
                            onDeepLink={(area) => onGoToArea(area as SetupArea)}
                        />
                    </>
                ) : (
                    /* NOT A GAP, AND THE COPY HAS TO SAY SO. A house vehicle owes no schedule —
                       `agreement_create` refuses to raise one and `get_contract_setup_status` emits
                       no arm over it — so an empty state worded like the two below it ("no partner
                       arc yet") would read as something outstanding on the 259 cars Primelane owns
                       itself. It reports the shape of the contract instead.

                       TWO SHAPES, THOUGH, AND THE SLOT'S ABSENCE CANNOT TELL THEM APART.
                       `isThirdPartyVehicle` is false for the house and false for a null, and a null
                       is a contract with no car on it — the state every contract opens in, before
                       the vehicle step. Naming the house there would assert an ownership nobody has
                       chosen yet. */
                    <p className="sd-hint">
                        {vehicle.id === null
                            ? 'No vehicle is on this contract yet, so there is no owner to sign a schedule with.'
                            : 'Primelane owns this vehicle, so there is no partner to sign a schedule with.'}
                    </p>
                )}
            </Block>

            <Block id="b-hpa" title="Hire purchase agreement" aside="master instrument">
                {/* THE BOARD'S OWN REASON, MATCHED RATHER THAN RE-DERIVED [D-B48].
                    `buildBoardSlots` hangs `PARTNER_SCHEDULE_LOCK_REASON` on this slot while the
                    vehicle's partner has not executed a schedule, and comparing against that
                    exported constant is what keeps the lock line and the padlock in step: the slot
                    carries ONE reason at a time and several rules can set it (the cash price is the
                    other on this arc), so a line drawn from a second reading of the partner's
                    paperwork would appear beside a row shut for a different cause. */}
                {hpa?.blockedReason === PARTNER_SCHEDULE_LOCK_REASON ? (
                    <LockLine title="Locked until the partner signs the vehicle schedule">
                        Primelane deploys a partner’s vehicle to a driver only under a schedule that
                        partner has signed, so the driver’s signature on this agreement is refused
                        until the schedule above is executed — and generating it early would put a
                        document in the driver’s inbox that cannot be signed and whose reason they
                        cannot see. The schedule is the first move on this contract.
                    </LockLine>
                ) : null}
                <p className="sd-lede sd-dim">
                    The driver signs it, and their signature is what opens the witness agreement.
                    Eighteen merge fields drawn from four areas — the pre-flight below names any
                    that are still unresolved and links to the area that can fill each one.
                </p>
                {hpa ? (
                    <AgreementSlotList
                        slots={[hpa]}
                        agreements={agreements}
                        onDeepLink={(area) => onGoToArea(area as SetupArea)}
                    />
                ) : (
                    <p className="sd-hint">This contract has no hire purchase arc yet.</p>
                )}
            </Block>

            <Block id="b-witnessagr" title="Witness agreement">
                {witnessLocked ? (
                    <LockLine title="Locked until the driver signs the hire purchase agreement">
                        The witness agreement prints the date the driver signed theirs, and that date
                        does not exist until they have.{' '}
                        {hpa?.document ? (
                            <>
                                <b>
                                    {hpaSigned} of {hpaSignatures.length}
                                </b>{' '}
                                signatures are in.
                            </>
                        ) : (
                            'The hire purchase agreement has not been generated yet.'
                        )}{' '}
                        Primelane cannot countersign the hire purchase agreement either until this
                        one has been signed, so the driver’s signature is what moves both. A paper
                        original already on file against the hire purchase agreement unlocks this in
                        the same way — the signature is what matters, not the document.
                    </LockLine>
                ) : witnessSlot ? (
                    <>
                        <p className="sd-lede sd-dim">
                            {hpaPaper
                                ? 'The hire purchase agreement was recorded on paper, which sets the signing date the witness template needs. This can be sent now.'
                                : 'The driver has signed the hire purchase agreement. This can be sent now, and Primelane countersigns both once it comes back.'}
                        </p>
                        <AgreementSlotList
                            slots={[witnessSlot]}
                            agreements={agreements}
                            onDeepLink={(area) => onGoToArea(area as SetupArea)}
                        />
                    </>
                ) : (
                    <p className="sd-hint">This contract has no witness arc yet.</p>
                )}
            </Block>

            <Block id="b-others" title="Other instruments" aside="read-only mirrors">
                <Rows>
                    {/* `· required` and not `· optional` [D-B15]: a deposit slot exists exactly
                        where a payment is linked, and every linked payment has to be papered before
                        the contract can be activated. NO SLOT AT ALL IS NOT A GAP [D-B41] — a
                        contract that took no deposit activates without one — so the empty state
                        reports the shape of the contract and never something outstanding. */}
                    <Rec
                        label="Deposit agreement"
                        value={
                            depositSlots.length === 0
                                ? null
                                : depositSlots.map(mirrorState).filter(Boolean).join(' · ') ||
                                  'Not started · required'
                        }
                        sub={
                            <LinkLike onClick={() => onGoToArea('deposit')}>Open Deposit</LinkLike>
                        }
                        empty="No deposit linked"
                    />
                    {/* `· required` and not `· optional` [D-B14], on the deposit's rule exactly:
                        an insurance-loan slot exists only where a premium is recorded, and a
                        recorded premium is one the server will not activate the contract without a
                        signed document over — whichever of the two it is [D-B45]. */}
                    {/* NAMED FOR THE INSTRUMENT THE SLOTS ARE ON [D-B45]. `INSURANCE_LOAN`
                        publishes two — an Insurance Premium Advance for a financed year, an
                        Insurance Premium Payment Record for one the driver paid in full — and this
                        row is a MIRROR: it has to name the same document the insurance area does,
                        or the two blocks read as two instruments. SENTENCE CASE off the slot's
                        INSTRUMENT rather than off `slot.title`, which carries the published
                        Title Case an H1 wants and this rail's four labels do not — the same two
                        strings `setupState.labelOf` picks between for the manifest row. */}
                    <Rec
                        label={
                            insuranceSlots[0]?.instrument === 'PAYMENT_RECORD'
                                ? 'Insurance premium payment record'
                                : 'Insurance premium advance'
                        }
                        value={
                            insuranceSlots.length === 0
                                ? null
                                : insuranceSlots.map(mirrorState).filter(Boolean).join(' · ') ||
                                  'Not started · required'
                        }
                        sub={
                            <LinkLike onClick={() => onGoToArea('insurance')}>Open Insurance</LinkLike>
                        }
                        empty="Nothing to paper"
                    />
                    {/* NO `Advance #n` ROWS, AND NO WAY BACK TO INSIGHTS FROM HERE [D-B17]. A
                        standing operational expense advance of this driver's is not part of
                        setting this contract up — it is opened whenever they borrow, usually years
                        after the hire purchase agreement was signed — so it is neither mirrored
                        here nor minted as a slot on THIS board: the subjects read fetches no
                        `loans` at setup scope. Its Schedule is executed in the app from Contract
                        Insights (owner's ruling, 2026-09-16), which is a door of its own and not a
                        step of this one. Two instruments are mirrored on this block and both are
                        conditions of ACTIVATION: the deposit's paper [D-B15] and the insurance
                        loan's [D-B14]. */}
                </Rows>
            </Block>
        </Blocks>
    );
}
